import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";

import {
  CheckCircle,
  Cancel,
  Search,
  Clear,
} from "@material-ui/icons";

import { AuctionList } from "../../../adminModel/auctions";


// =====================================================
// STYLES
// =====================================================

const useStyles = makeStyles((theme) => ({
  filterCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: "1px solid #e6e6e6",
    borderRadius: 8,
    backgroundColor: "#fff",
  },

  filterTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#333",
    marginBottom: theme.spacing(1.5),
  },

  filterInput: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      fontSize: 13,
    },

    "& .MuiInputLabel-outlined": {
      fontSize: 13,
    },
  },

  filterButton: {
    height: 40,
    textTransform: "none",
    fontWeight: 600,
  },

  tableCard: {
    border: "1px solid #e6e6e6",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#fff",
  },

  tableContainer: {
    overflowX: "auto",
  },

  table: {
    minWidth: 850,
  },

  tableHeader: {
    backgroundColor: "#f7f7f8",
  },

  headerCell: {
    fontSize: 12,
    fontWeight: 700,
    color: "#444",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    padding: "14px 16px",
    borderBottom: "1px solid #e0e0e0",
    whiteSpace: "nowrap",
  },

  bodyCell: {
    fontSize: 13,
    color: "#333",
    padding: "13px 16px",
    borderBottom: "1px solid #eeeeee",
    verticalAlign: "middle",
  },

  tableRow: {
    "&:hover": {
      backgroundColor: "#fafafa",
    },
  },

  auctionLink: {
    padding: 0,
    minWidth: 0,
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    justifyContent: "flex-start",
    textAlign: "left",
  },

  statusActive: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 20,
    backgroundColor: "#edf7ed",
    color: "#2e7d32",
    fontSize: 12,
    fontWeight: 600,
  },

  statusInactive: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 20,
    backgroundColor: "#f5f5f5",
    color: "#777",
    fontSize: 12,
    fontWeight: 600,
  },

  liveIcon: {
    color: theme.palette.success.main,
    fontSize: 20,
  },

  notLiveIcon: {
    color: "#bdbdbd",
    fontSize: 20,
  },

  emptyState: {
    padding: theme.spacing(7),
    textAlign: "center",
    color: "#888",
  },

  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2.5),
    borderTop: "1px solid #eeeeee",
    gap: theme.spacing(2),
  },

  paginationButton: {
    minWidth: 100,
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 600,
  },

  pageNumber: {
    minWidth: 70,
    textAlign: "center",
    fontSize: 13,
    fontWeight: 600,
    color: "#555",
  },

  resultCount: {
    fontSize: 12,
    color: "#888",
  },
}));


// =====================================================
// AUCTION ROW
// =====================================================

const AuctionRow = ({ auction }) => {
  const classes = useStyles();

  const handleViewAuction = () => {
    window.location.href =
      `/app/auctions/view/details/${encodeURIComponent(
        auction.auction_name
      )}/${auction.id}`;
  };

  const isActive = auction.auction_status === 1;

  return (
    <TableRow
      hover
      className={classes.tableRow}
    >

      {/* AUCTION NAME */}

      <TableCell className={classes.bodyCell}>
        <Button
          className={classes.auctionLink}
          color="primary"
          onClick={handleViewAuction}
        >
          {auction.auction_name || "N/A"}
        </Button>
      </TableCell>


      {/* START DATE */}

      <TableCell className={classes.bodyCell}>
        {auction.date_start
          ? auction.date_start.split("T")[0]
          : "-"}
      </TableCell>


      {/* AUCTION TYPE */}

      <TableCell className={classes.bodyCell}>
        {auction.auctionType || "-"}
      </TableCell>


      {/* STATUS */}

      <TableCell className={classes.bodyCell}>
        <span
          className={
            isActive
              ? classes.statusActive
              : classes.statusInactive
          }
        >
          {isActive ? "Active" : "Inactive"}
        </span>
      </TableCell>


      {/* LIVE */}

      <TableCell
        className={classes.bodyCell}
        align="center"
      >
        {auction.isLive === 1 ? (
          <CheckCircle
            className={classes.liveIcon}
          />
        ) : (
          <Cancel
            className={classes.notLiveIcon}
          />
        )}
      </TableCell>

    </TableRow>
  );
};


// =====================================================
// MAIN
// =====================================================

export default function AuctionTable() {
  const classes = useStyles();

  const [loading, setLoading] =
    useState(false);

  const [auctions, setAuctions] =
    useState([]);

  const [count, setCount] =
    useState(0);

  const [page, setPage] =
    useState(0);

  const limit = 10;


  // ===================================================
  // FILTER STATE
  // ===================================================

  const [filters, setFilters] = useState({
    auctionName: "",
    startDate: "",
    auctionType: "",
    status: "",
    isLive: "",
  });


  const [searchFilters, setSearchFilters] =
    useState(filters);


  // ===================================================
  // FETCH AUCTIONS
  // ===================================================

  const fetchAuction = useCallback(
    async (
      filtersObj = searchFilters,
      pageNum = page
    ) => {

      setLoading(true);

      const search =
        filtersObj.auctionName || "";

      const start =
        pageNum * limit;

      try {

        const results =
          await AuctionList(
            search,
            start
          );


        if (
          !results ||
          results.success === false ||
          !results.data
        ) {
          setAuctions([]);
          setCount(0);
          return;
        }


        let data = results.data;


        if (!Array.isArray(data)) {
          setAuctions([]);
          setCount(0);
          return;
        }


        // START DATE

        if (filtersObj.startDate) {
          data = data.filter(
            (auction) =>
              auction.date_start?.startsWith(
                filtersObj.startDate
              )
          );
        }


        // AUCTION TYPE

        if (filtersObj.auctionType) {
          data = data.filter(
            (auction) =>
              String(
                auction.auctionType || ""
              ).toLowerCase() ===
              String(
                filtersObj.auctionType
              ).toLowerCase()
          );
        }


        // STATUS

        if (filtersObj.status) {

          if (
            filtersObj.status === "active"
          ) {
            data = data.filter(
              (auction) =>
                auction.auction_status === 1
            );
          }

          if (
            filtersObj.status === "inactive"
          ) {
            data = data.filter(
              (auction) =>
                auction.auction_status !== 1
            );
          }
        }


        // LIVE

        if (filtersObj.isLive !== "") {
          data = data.filter(
            (auction) =>
              String(
                auction.isLive
              ) === String(
                filtersObj.isLive
              )
          );
        }


        setAuctions(data);

        setCount(
          results.count || 0
        );

      } catch (error) {

        console.error(
          "Fetch Error:",
          error
        );

        setAuctions([]);
        setCount(0);

      } finally {

        setLoading(false);

      }
    },
    [searchFilters, page]
  );


  useEffect(() => {

    fetchAuction(
      searchFilters,
      page
    );

  }, [
    fetchAuction,
    searchFilters,
    page,
  ]);


  // ===================================================
  // FILTER HANDLERS
  // ===================================================

  const handleFilterChange =
    (field) => (event) => {

      const value =
        event.target.value;

      setFilters((previous) => ({
        ...previous,
        [field]: value,
      }));

    };


  const handleSearch = () => {

    setPage(0);

    setSearchFilters({
      ...filters,
    });

  };


  const handleClear = () => {

    const emptyFilters = {
      auctionName: "",
      startDate: "",
      auctionType: "",
      status: "",
      isLive: "",
    };

    setFilters(emptyFilters);

    setSearchFilters(emptyFilters);

    setPage(0);

  };


  // ===================================================
  // PAGINATION
  // ===================================================

  const totalPages =
    Math.ceil(count / limit);


  // ===================================================
  // RENDER
  // ===================================================

  return (
    <Box>


      {/* =================================================
          FILTER
      ================================================= */}

      <Paper
        className={classes.filterCard}
        elevation={0}
      >

        <Typography
          className={classes.filterTitle}
        >
          Filter Auctions
        </Typography>


        <Box
          display="flex"
          flexWrap="wrap"
          gridGap={12}
          alignItems="center"
        >

          {/* SEARCH */}

          <Box
            flex="1 1 260px"
            minWidth={220}
          >
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Auction Name"
              placeholder="Search auction..."
              value={
                filters.auctionName
              }
              onChange={
                handleFilterChange(
                  "auctionName"
                )
              }
              className={
                classes.filterInput
              }
            />
          </Box>


          {/* DATE */}

          <Box
            flex="1 1 180px"
            minWidth={170}
          >
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              type="date"
              label="Start Date"
              value={
                filters.startDate
              }
              onChange={
                handleFilterChange(
                  "startDate"
                )
              }
              InputLabelProps={{
                shrink: true,
              }}
              className={
                classes.filterInput
              }
            />
          </Box>


          {/* AUCTION TYPE */}

          <Box
            flex="1 1 180px"
            minWidth={170}
          >
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Auction Type"
              placeholder="Auction type"
              value={
                filters.auctionType
              }
              onChange={
                handleFilterChange(
                  "auctionType"
                )
              }
              className={
                classes.filterInput
              }
            />
          </Box>


          {/* STATUS */}

          <Box
            flex="1 1 150px"
            minWidth={140}
          >
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              className={
                classes.filterInput
              }
            >

              <InputLabel>
                Status
              </InputLabel>

              <Select
                label="Status"
                value={
                  filters.status
                }
                onChange={
                  handleFilterChange(
                    "status"
                  )
                }
              >

                <MenuItem value="">
                  All
                </MenuItem>

                <MenuItem value="active">
                  Active
                </MenuItem>

                <MenuItem value="inactive">
                  Inactive
                </MenuItem>

              </Select>

            </FormControl>
          </Box>


          {/* LIVE */}

          <Box
            flex="1 1 140px"
            minWidth={130}
          >
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              className={
                classes.filterInput
              }
            >

              <InputLabel>
                Live
              </InputLabel>

              <Select
                label="Live"
                value={
                  filters.isLive
                }
                onChange={
                  handleFilterChange(
                    "isLive"
                  )
                }
              >

                <MenuItem value="">
                  All
                </MenuItem>

                <MenuItem value="1">
                  Live
                </MenuItem>

                <MenuItem value="0">
                  Not Live
                </MenuItem>

              </Select>

            </FormControl>
          </Box>


          {/* SEARCH BUTTON */}

          <Button
            variant="contained"
            color="primary"
            startIcon={
              <Search />
            }
            className={
              classes.filterButton
            }
            onClick={handleSearch}
          >
            Search
          </Button>


          {/* CLEAR BUTTON */}

          <Button
            variant="outlined"
            startIcon={
              <Clear />
            }
            className={
              classes.filterButton
            }
            onClick={handleClear}
          >
            Clear
          </Button>

        </Box>

      </Paper>


      {/* =================================================
          AUCTION TABLE
      ================================================= */}

      <Paper
        className={classes.tableCard}
        elevation={0}
      >

        <TableContainer
          className={
            classes.tableContainer
          }
        >

          <Table
            size="small"
            className={classes.table}
          >

            <TableHead>

              <TableRow
                className={
                  classes.tableHeader
                }
              >

                <TableCell
                  className={
                    classes.headerCell
                  }
                >
                  Auction Name
                </TableCell>

                <TableCell
                  className={
                    classes.headerCell
                  }
                >
                  Start Date
                </TableCell>

                <TableCell
                  className={
                    classes.headerCell
                  }
                >
                  Auction Type
                </TableCell>

                <TableCell
                  className={
                    classes.headerCell
                  }
                >
                  Status
                </TableCell>

                <TableCell
                  className={
                    classes.headerCell
                  }
                  align="center"
                >
                  Live
                </TableCell>

              </TableRow>

            </TableHead>


            <TableBody>

              {loading ? (

                <TableRow>

                  <TableCell
                    colSpan={5}
                    align="center"
                    className={
                      classes.emptyState
                    }
                  >

                    <CircularProgress
                      size={28}
                    />

                  </TableCell>

                </TableRow>

              ) : auctions.length === 0 ? (

                <TableRow>

                  <TableCell
                    colSpan={5}
                    className={
                      classes.emptyState
                    }
                  >

                    <Typography
                      variant="body2"
                    >
                      No auctions found.
                    </Typography>

                  </TableCell>

                </TableRow>

              ) : (

                auctions.map(
                  (auction) => (
                    <AuctionRow
                      key={auction.id}
                      auction={auction}
                    />
                  )
                )

              )}

            </TableBody>

          </Table>

        </TableContainer>


        {/* =================================================
            PAGINATION
        ================================================= */}

        <Box
          className={
            classes.pagination
          }
        >

          <Button
            variant="outlined"
            disabled={
              page === 0 ||
              loading
            }
            onClick={() =>
              setPage(
                (previous) =>
                  previous - 1
              )
            }
            className={
              classes.paginationButton
            }
          >
            Previous
          </Button>


          <Typography
            className={
              classes.pageNumber
            }
          >
            Page {page + 1} /{" "}
            {totalPages || 1}
          </Typography>


          <Button
            variant="contained"
            color="primary"
            disabled={
              loading ||
              (page + 1) * limit >= count
            }
            onClick={() =>
              setPage(
                (previous) =>
                  previous + 1
              )
            }
            className={
              classes.paginationButton
            }
          >
            Next
          </Button>

        </Box>

      </Paper>

    </Box>
  );
}